import type { MeResponse, WorkspaceSummary } from '@nolens/domain';
import { Button, IconButton, Symbol, useLocale } from '@nolens/ui';
import { Fragment, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { linkAfterSignIn, openPanelOnExtension } from '../lib/extension';
import { macLinked } from '../lib/mac';
import { spaceName } from '../lib/locale';
import { activeSpace, rememberSpace, useIsDesktop, usePref } from '../lib/prefs';
import { publicPaths } from '../i18n/landing';
import { shellText } from '../i18n/shell';
import { ChevronRightIcon, MenuIcon, PanelIcon, RecordIcon, SearchIcon, SparkIcon } from './Icons';
import { PlanLimitNotice } from './PlanLimit';
import { RecordDialog, useExtensionState } from './RecordDialog';
import { SearchDialog } from './SearchDialog';
import { ShellContext, useShell } from './shellContext';
import { Bell, SettingsSidebar, Sidebar } from './Sidebar';
import { SpaceTile } from './SpaceTile';

/** Um passo do caminho na barra de topo («Apoio ao cliente / Definições / Marca»). */
export interface Crumb {
  label: string;
  to?: string;
  /** O primeiro passo pode ser um espaço: mostra o quadrado dele. */
  space?: WorkspaceSummary;
}

const SIDEBAR = ['open', 'hidden'] as const;

/**
 * Estrutura da app com sessão: barra lateral a toda a altura (no telemóvel, numa gaveta) e
 * barra de topo com o caminho da página e «Gravar». `settings` troca a barra lateral pela
 * das definições.
 */
export function AppShell({
  me,
  children,
  current,
  crumbs,
  settings,
}: {
  me: MeResponse;
  children: ReactNode;
  current?: string;
  crumbs?: Crumb[];
  settings?: boolean;
}) {
  const locale = useLocale();
  const email = me.user.email;
  useEffect(() => void linkAfterSignIn(email), [email]);
  const ws = activeSpace(me, current);
  const here = me.workspaces.some((w) => w.id === current) ? current : undefined;
  useEffect(() => {
    if (here) rememberSpace(here);
  }, [here]);
  const desktop = useIsDesktop();
  const [sidebar, setSidebar] = usePref('nolens:sidebar', SIDEBAR, 'open');
  const [record, setRecord] = useState(false);
  const [search, setSearch] = useState(false);
  // Já conhecida antes do clique em «Gravar»: a barra lateral só abre no próprio clique
  const ext = useExtensionState(email);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearch(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const shell = useMemo(
    () => ({
      // Com a extensão pronta, abre a barra lateral; senão (ou se não abrir), diz o que fazer.
      // Com a app de Mac ligada, pergunta primeiro onde gravar.
      openRecord: () => {
        if (ext !== 'ready' || macLinked(me)) return setRecord(true);
        void openPanelOnExtension().then((ok) => {
          if (!ok) setRecord(true);
        });
      },
      openSearch: () => setSearch(true),
    }),
    [ext, me],
  );
  const nav = settings ? (
    <SettingsSidebar me={me} ws={ws} />
  ) : (
    <Sidebar me={me} ws={ws} bell={desktop} />
  );
  const path =
    crumbs ?? (here ? [{ label: spaceName(ws, locale), to: `/w/${ws.id}`, space: ws }] : []);
  return (
    <ShellContext.Provider value={shell}>
      <div className="flex min-h-dvh">
        {desktop && sidebar === 'open' && (
          <div
            className="no-print sticky top-0 h-dvh w-[252px] shrink-0 border-r border-border bg-surface-1"
            data-sidebar
          >
            {nav}
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar
            ws={ws}
            crumbs={path}
            desktop={desktop}
            sidebarOpen={sidebar === 'open'}
            onToggleSidebar={() => setSidebar(sidebar === 'open' ? 'hidden' : 'open')}
            drawer={nav}
          />
          <main className="mx-auto w-full min-w-0 max-w-6xl flex-1 px-4 py-6 sm:px-6 md:px-8 md:py-8">
            {children}
          </main>
        </div>
      </div>
      <PlanLimitNotice />
      <RecordDialog me={me} open={record} onClose={() => setRecord(false)} />
      <SearchDialog me={me} open={search} onClose={() => setSearch(false)} />
    </ShellContext.Provider>
  );
}

function TopBar({
  ws,
  crumbs,
  desktop,
  sidebarOpen,
  onToggleSidebar,
  drawer,
}: {
  ws: WorkspaceSummary;
  crumbs: Crumb[];
  desktop: boolean;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  drawer: ReactNode;
}) {
  const locale = useLocale();
  const t = shellText(locale);
  const { openRecord, openSearch } = useShell();
  const upgrade = ws.role === 'owner' && (ws.plan === 'free' || ws.plan === 'expired');
  return (
    <header className="no-print sticky top-0 z-10 flex h-14 shrink-0 items-center gap-1.5 border-b border-border bg-surface-0/90 px-3 backdrop-blur sm:px-4">
      {desktop ? (
        <IconButton label={sidebarOpen ? t.hideSidebar : t.showSidebar} onClick={onToggleSidebar}>
          <PanelIcon />
        </IconButton>
      ) : (
        <MobileDrawer label={t.menu}>{drawer}</MobileDrawer>
      )}
      {(!desktop || !sidebarOpen) && (
        <Link to="/inicio" aria-label={t.home} className="mr-1 shrink-0">
          <Symbol size={22} />
        </Link>
      )}
      <Crumbs crumbs={crumbs} />
      <div className="flex-1" />
      {upgrade && (
        <Link
          // Uma equipa sem plano subscreve-se na sua página do plano
          to={ws.plan === 'expired' ? `/w/${ws.id}/definicoes/plano` : publicPaths(locale).pricing}
          className="hidden h-8 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium text-accent-text transition hover:bg-accent-soft sm:inline-flex"
          data-upgrade={ws.plan}
        >
          <SparkIcon size={15} />
          {ws.plan === 'free' ? t.tryPro : t.seePlans}
        </Link>
      )}
      {!desktop && (
        <>
          <IconButton label={t.searchGuides} onClick={openSearch}>
            <SearchIcon />
          </IconButton>
          <Bell align="right" />
        </>
      )}
      <Button size="sm" icon={<RecordIcon />} onClick={openRecord} className="ml-1" data-record>
        {t.record}
      </Button>
    </header>
  );
}

/** O caminho da página; no telemóvel, só o último passo. */
function Crumbs({ crumbs }: { crumbs: Crumb[] }) {
  const t = shellText(useLocale());
  if (!crumbs.length) return null;
  return (
    <nav aria-label={t.breadcrumb} className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1 text-[14px]">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          const body = (
            <>
              {c.space && <SpaceTile ws={c.space} size={20} />}
              <span className="truncate">{c.label}</span>
            </>
          );
          return (
            <Fragment key={i}>
              <li
                className={
                  last ? 'flex min-w-0 items-center' : 'hidden min-w-0 items-center md:flex'
                }
              >
                {c.to && !last ? (
                  <Link
                    to={c.to}
                    className="flex min-w-0 items-center gap-2 rounded-[8px] px-1.5 py-1 text-text-2 hover:bg-surface-2 hover:text-text-1"
                  >
                    {body}
                  </Link>
                ) : (
                  <span
                    aria-current={last ? 'page' : undefined}
                    className="flex min-w-0 items-center gap-2 px-1.5 py-1 font-medium text-text-1"
                  >
                    {body}
                  </span>
                )}
              </li>
              {!last && (
                <li aria-hidden="true" className="hidden text-text-3 md:block">
                  <ChevronRightIcon size={14} />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Telemóvel: a barra lateral numa gaveta à esquerda, sobre a página escurecida. Fecha ao
 * escolher uma ligação (a navegação não fecha o popover sozinha) e só existe aberta.
 */
function MobileDrawer({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton label={label} popoverTarget={id} data-mobile-menu>
        <MenuIcon size={20} />
      </IconButton>
      <div
        id={id}
        popover="auto"
        className="fixed inset-y-0 left-0 right-auto m-0 h-dvh max-h-none w-[min(18rem,86vw)] overflow-hidden border-0 border-r border-border bg-surface-1 p-0 shadow-xl backdrop:bg-[rgb(13_14_16/0.4)]"
        onToggle={(e) => setOpen(e.currentTarget.matches(':popover-open'))}
        onClick={(e) => {
          if ((e.target as Element).closest('a')) e.currentTarget.hidePopover();
        }}
      >
        {open && children}
      </div>
    </>
  );
}
